import { useMutation, useQuery } from '@apollo/client'
import {
    Autocomplete,
    Box,
    Button,
    Divider,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { type GridRowParams } from '@mui/x-data-grid-pro'
import StarterKit from '@tiptap/starter-kit'
import { type GetProcessStatusOptionsQuery } from '__generated__/graphql'
import { type FC, useState } from 'react'
import { RichTextReadOnly } from 'mui-tiptap'
import { gql } from '__generated__'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { AddToRoster } from 'recruiter/pages/Rosters/RosterProfile/CompareTable/Compare.gql'
import {
    GetFilteredRostersSansCandidate,
    GetFilteredRostersWithCandidate,
} from 'recruiter/contexts'
import useToast from 'shared/components/Toast/useToast'
import {
    formatGraphQLError,
    getPxAsRem,
    sortProcessStatusesBySummaryGroup,
} from 'shared/utils'
import { type CandidateRostersSansCandidateTableRowModel } from './CandidateRostersSansCandidateTable'

const GetRosterPitchNote = gql(/* GraphQL */ `
    query getRosterPitchNoteForPanel($id: ID!) {
        roster(id: $id) {
            pitchNote {
                note
            }
        }
    }
`)

interface AddToRosterPanelProps {
    data: GridRowParams<CandidateRostersSansCandidateTableRowModel>
    candidateId: string
}

type ProcessStatusOption = NonNullable<
    GetProcessStatusOptionsQuery['processStatuses'][number]
>

const AddToRosterPanel: FC<AddToRosterPanelProps> = ({ data, candidateId }) => {
    const toast = useToast()
    const rosterId = data.row.roster?.id ?? ''
    const canEdit = data.row.permissions?.canEdit ?? false

    const [selectedProcessStatus, setSelectedProcessStatus] =
        useState<ProcessStatusOption | null>(null)
    const [internalNote, setInternalNote] = useState('')
    const [externalNote, setExternalNote] = useState('')

    const { data: pitchNoteData, loading: pitchNoteLoading } = useQuery(
        GetRosterPitchNote,
        {
            variables: { id: rosterId },
            skip: !rosterId,
        }
    )

    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)

    const [addToRoster, { loading: addToRosterLoading }] = useMutation(
        AddToRoster,
        {
            refetchQueries: [
                GetFilteredRostersSansCandidate,
                GetFilteredRostersWithCandidate,
            ],
            onCompleted(result) {
                if (result.addCandidatesToRoster.success) {
                    toast({
                        title: 'Success',
                        message:
                            result.addCandidatesToRoster.message ||
                            'Candidate added to roster successfully',
                        severity: 'success',
                    })
                    setSelectedProcessStatus(null)
                    setInternalNote('')
                    setExternalNote('')
                } else {
                    toast({
                        title: 'Error',
                        message:
                            result.addCandidatesToRoster.message ||
                            'Error adding candidate to roster',
                        severity: 'error',
                    })
                }
            },
            onError(error) {
                toast({
                    title: 'Error',
                    message: formatGraphQLError(
                        error,
                        'Error adding candidate to roster'
                    ),
                    severity: 'error',
                })
            },
        }
    )

    const handleAddToRoster = () => {
        if (!selectedProcessStatus) {
            toast({
                title: 'Error',
                message: 'Please select a process status',
                severity: 'error',
            })
            return
        }

        addToRoster({
            variables: {
                candidateIds: [candidateId],
                rosterId,
                processStatusId: selectedProcessStatus.id,
                internalNote: internalNote || undefined,
                externalNote: externalNote || undefined,
            },
        })
    }

    return (
        <Stack sx={{ p: getPxAsRem(16, 24) }} spacing={2}>
            <Box color="GrayText">
                {pitchNoteLoading ? (
                    <Skeleton variant="text" />
                ) : (
                    <RichTextReadOnly
                        extensions={[StarterKit]}
                        content={pitchNoteData?.roster?.pitchNote?.note ?? '-'}
                    />
                )}
            </Box>

            <Divider />

            <Stack direction="row" flexWrap="wrap" gap={2}>
                <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        Internal
                    </Typography>
                    <TextField
                        variant="outlined"
                        label="Note for CPI"
                        placeholder="Add feedback here..."
                        multiline
                        rows={6}
                        fullWidth
                        value={internalNote}
                        onChange={(e) => setInternalNote(e.target.value)}
                        disabled={!canEdit}
                        sx={{ minHeight: getPxAsRem(150) }}
                    />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        Client Visible
                    </Typography>
                    <TextField
                        variant="outlined"
                        label="Note for Client"
                        placeholder="Add feedback here..."
                        multiline
                        rows={6}
                        fullWidth
                        value={externalNote}
                        onChange={(e) => setExternalNote(e.target.value)}
                        disabled={!canEdit}
                        sx={{ minHeight: getPxAsRem(150) }}
                    />
                </Box>
            </Stack>

            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
            >
                <Autocomplete
                    disabled={!canEdit}
                    value={selectedProcessStatus}
                    onChange={(_, value) => setSelectedProcessStatus(value)}
                    options={sortProcessStatusesBySummaryGroup(
                        processStatusOptions?.processStatuses ?? []
                    )}
                    getOptionLabel={(option) => option.statusLabel}
                    isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                    }
                    groupBy={(option) =>
                        option.processStatusSummaryGroup?.label || ''
                    }
                    sx={{ minWidth: 250 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Process Status"
                            placeholder="Select a process status"
                            size="small"
                        />
                    )}
                />
                <Button
                    variant="contained"
                    onClick={handleAddToRoster}
                    disabled={
                        !canEdit || !selectedProcessStatus || addToRosterLoading
                    }
                >
                    Add to Roster
                </Button>
            </Stack>
        </Stack>
    )
}

export default AddToRosterPanel
